import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../components/ui/select';
import { 
    Wallet,
    DollarSign,
    ArrowUpCircle,
    ArrowDownCircle,
    Clock,
    CheckCircle,
    XCircle,
    Plus,
    Minus,
    Receipt,
    Building2,
    Calculator,
    Lock,
    Unlock,
    History,
    AlertTriangle,
    RefreshCw,
    Loader2,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOVEMENT_TYPES = {
    abertura: { label: 'Abertura', color: 'bg-blue-500/20 text-blue-500 border-blue-500/30', icon: Unlock },
    fechamento: { label: 'Fechamento', color: 'bg-purple-500/20 text-purple-500 border-purple-500/30', icon: Lock },
    venda_dinheiro: { label: 'Venda (Dinheiro)', color: 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30', icon: ArrowUpCircle },
    venda_pix_banco: { label: 'Venda PIX (BANCO)', color: 'bg-cyan-500/20 text-cyan-500 border-cyan-500/30', icon: ArrowUpCircle },
    venda_cartao_banco: { label: 'Venda Cartão (BANCO)', color: 'bg-indigo-500/20 text-indigo-500 border-indigo-500/30', icon: ArrowUpCircle },
    troco: { label: 'Troco', color: 'bg-amber-500/20 text-amber-500 border-amber-500/30', icon: ArrowDownCircle },
    reforco: { label: 'Reforço', color: 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30', icon: Plus },
    sangria: { label: 'Retirada', color: 'bg-red-500/20 text-red-500 border-red-500/30', icon: Minus },
    despesa: { label: 'Despesa', color: 'bg-orange-500/20 text-orange-500 border-orange-500/30', icon: Receipt }
};

const CashRegisterPage = () => {
    const { isAdmin, isAtendente } = useAuth();
    const [currentRegister, setCurrentRegister] = useState(null);
    const [movements, setMovements] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    
    // Dialogs
    const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
    const [closeRegisterDialog, setCloseRegisterDialog] = useState(false);
    const [movementDialog, setMovementDialog] = useState(false);
    const [historyDialog, setHistoryDialog] = useState(false);
    const [detailsDialog, setDetailsDialog] = useState(false);
    const [selectedRegister, setSelectedRegister] = useState(null);
    
    // Form states
    const [initialBalance, setInitialBalance] = useState('');
    const [countedBalance, setCountedBalance] = useState('');
    const [movementType, setMovementType] = useState('reforco');
    const [movementAmount, setMovementAmount] = useState('');
    const [movementReason, setMovementReason] = useState('');
    const [movementObservation, setMovementObservation] = useState('');

    // Calculate CAIXA (physical cash) and BANCO (PIX/Card) totals
    const { totalCaixa, totalBanco } = useMemo(() => {
        let caixa = 0;
        let banco = 0;
        
        movements.forEach(m => {
            if (m.movement_type === 'venda_pix_banco' || m.movement_type === 'venda_cartao_banco') {
                banco += m.amount;
            } else if (m.amount > 0 && !['abertura', 'fechamento'].includes(m.movement_type)) {
                // Cash entries (excluding opening/closing)
                caixa += m.amount;
            }
        });
        
        return { totalCaixa: caixa, totalBanco: banco };
    }, [movements]);

    const fetchCurrentRegister = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/api/cash-register/current`);
            setCurrentRegister(response.data);
            if (response.data) {
                fetchMovements(response.data.id);
            }
        } catch (error) {
            console.error('Error fetching current register:', error);
        }
    }, []);

    const fetchMovements = async (registerId) => {
        try {
            const response = await axios.get(`${API_URL}/api/cash-register/movements?cash_register_id=${registerId}`);
            setMovements(response.data);
        } catch (error) {
            console.error('Error fetching movements:', error);
        }
    };

    const fetchHistory = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/cash-register/history?limit=30`);
            setHistory(response.data);
        } catch (error) {
            console.error('Error fetching history:', error);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await fetchCurrentRegister();
            await fetchHistory();
            setLoading(false);
        };
        loadData();
    }, [fetchCurrentRegister]);

    const handleOpenRegister = async () => {
        if (!initialBalance || parseFloat(initialBalance) < 0) {
            toast.error('Informe o valor inicial do caixa');
            return;
        }

        setProcessing(true);
        try {
            const response = await axios.post(`${API_URL}/api/cash-register/open`, {
                initial_balance: parseFloat(initialBalance)
            });
            setCurrentRegister(response.data);
            setMovements([]);
            toast.success('Caixa aberto com sucesso!');
            setOpenRegisterDialog(false);
            setInitialBalance('');
            fetchHistory();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao abrir caixa');
        } finally {
            setProcessing(false);
        }
    };

    const handleCloseRegister = async () => {
        if (!countedBalance || parseFloat(countedBalance) < 0) {
            toast.error('Informe o valor contado no caixa');
            return;
        }

        // Atendente não pode fechar caixa com divergência
        if (isAtendente()) {
            const difference = parseFloat(countedBalance) - currentRegister.expected_balance;
            if (difference !== 0) {
                toast.error('Caixa com divergência! Apenas um administrador pode fechar este caixa.');
                return;
            }
        }

        setProcessing(true);
        try {
            const response = await axios.post(`${API_URL}/api/cash-register/close`, {
                counted_balance: parseFloat(countedBalance)
            });
            toast.success('Caixa fechado com sucesso!');
            setCurrentRegister(null);
            setMovements([]);
            setCloseRegisterDialog(false);
            setCountedBalance('');
            fetchHistory();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao fechar caixa');
        } finally {
            setProcessing(false);
        }
    };

    const handleCreateMovement = async () => {
        if (!movementAmount || parseFloat(movementAmount) <= 0) {
            toast.error('Informe o valor da movimentação');
            return;
        }
        if (!movementReason.trim()) {
            toast.error('Informe o motivo da movimentação');
            return;
        }

        setProcessing(true);
        try {
            await axios.post(`${API_URL}/api/cash-register/movement`, {
                movement_type: movementType,
                amount: parseFloat(movementAmount),
                reason: movementReason,
                observation: movementObservation || null
            });
            toast.success('Movimentação registrada!');
            setMovementDialog(false);
            resetMovementForm();
            fetchCurrentRegister();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao registrar movimentação');
        } finally {
            setProcessing(false);
        }
    };

    const resetMovementForm = () => {
        setMovementType('reforco');
        setMovementAmount('');
        setMovementReason('');
        setMovementObservation('');
    };

    const openRegisterDetails = async (register) => {
        setSelectedRegister(register);
        try {
            const response = await axios.get(`${API_URL}/api/cash-register/${register.id}/summary`);
            setSelectedRegister(response.data);
            setDetailsDialog(true);
        } catch (error) {
            toast.error('Erro ao carregar detalhes');
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('pt-BR');
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="w-12 h-12 border-4 border-rr-yellow-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-4 lg:space-y-6 animate-fade-in-up" data-testid="cash-register-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white">Caixa</h1>
                    <p className="text-gray-400 text-sm lg:text-base mt-1">Controle de caixa diário</p>
                </div>
                <div className="flex items-center gap-2 lg:gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        className="border-[#262626] text-gray-400 h-8 lg:h-9"
                        onClick={() => { fetchCurrentRegister(); fetchHistory(); }}
                    >
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        className="border-[#262626] text-gray-400 h-8 lg:h-9"
                        onClick={() => setHistoryDialog(true)}
                    >
                        <History className="w-4 h-4 lg:mr-2" />
                        <span className="hidden lg:inline">Histórico</span>
                    </Button>
                </div>
            </div>

            {/* Status Card */}
            <Card className={`${currentRegister ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-red-500/5 border-red-500/30'}`}>
                <CardContent className="p-4 lg:p-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3 lg:gap-4">
                            <div className={`w-12 h-12 lg:w-16 lg:h-16 rounded-full flex items-center justify-center ${currentRegister ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                                {currentRegister ? (
                                    <Unlock className="w-6 h-6 lg:w-8 lg:h-8 text-emerald-500" />
                                ) : (
                                    <Lock className="w-6 h-6 lg:w-8 lg:h-8 text-red-500" />
                                )}
                            </div>
                            <div>
                                <div className="flex flex-wrap items-center gap-2 lg:gap-3">
                                    <Badge className={`text-xs lg:text-sm ${currentRegister ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30' : 'bg-red-500/20 text-red-500 border-red-500/30'}`}>
                                        {currentRegister ? 'ABERTO' : 'FECHADO'}
                                    </Badge>
                                    {currentRegister && (
                                        <span className="text-xs lg:text-sm text-gray-500">
                                            desde {formatDateTime(currentRegister.opened_at)}
                                        </span>
                                    )}
                                </div>
                                {currentRegister && (
                                    <p className="text-xs lg:text-sm text-gray-400 mt-1">
                                        Por: {currentRegister.opened_by_name}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="w-full sm:w-auto">
                            {currentRegister ? (
                                <Button
                                    className="bg-red-500 hover:bg-red-400 text-white w-full sm:w-auto"
                                    onClick={() => setCloseRegisterDialog(true)}
                                >
                                    <Lock className="w-4 h-4 mr-2" />
                                    Fechar Caixa
                                </Button>
                            ) : (
                                <Button
                                    className="bg-rr-yellow-500 hover:bg-rr-yellow-400 text-black font-bold w-full sm:w-auto"
                                    onClick={() => setOpenRegisterDialog(true)}
                                >
                                    <Unlock className="w-4 h-4 mr-2" />
                                    Abrir Caixa
                                </Button>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Current Register Info */}
            {currentRegister && (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Card className="bg-[#121212] border-[#262626]">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-md bg-blue-500/20 flex items-center justify-center">
                                        <Wallet className="w-5 h-5 text-blue-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider">Fundo Inicial</p>
                                        <p className="text-lg font-bold text-white">{formatCurrency(currentRegister.initial_balance)}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-[#121212] border-[#262626]">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-md bg-emerald-500/20 flex items-center justify-center">
                                        <ArrowUpCircle className="w-5 h-5 text-emerald-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider">Entradas</p>
                                        <p className="text-lg font-bold text-emerald-500">{formatCurrency(currentRegister.total_entries)}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-[#121212] border-[#262626]">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-md bg-red-500/20 flex items-center justify-center">
                                        <ArrowDownCircle className="w-5 h-5 text-red-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider">Saídas</p>
                                        <p className="text-lg font-bold text-red-500">{formatCurrency(currentRegister.total_exits)}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-[#121212] border-rr-yellow-500/30">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-md bg-rr-yellow-500/20 flex items-center justify-center">
                                        <Calculator className="w-5 h-5 text-rr-yellow-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider">Saldo Esperado</p>
                                        <p className="text-lg font-bold text-rr-yellow-500">{formatCurrency(currentRegister.expected_balance)}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* CAIXA vs BANCO Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card className="bg-emerald-500/5 border-emerald-500/30">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-md bg-emerald-500/20 flex items-center justify-center">
                                        <Wallet className="w-6 h-6 text-emerald-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wider">💵 Vendas em Dinheiro (CAIXA)</p>
                                        <p className="text-2xl font-bold text-emerald-500">{formatCurrency(totalCaixa)}</p>
                                        <p className="text-xs text-gray-500">Dinheiro físico no caixa</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-cyan-500/5 border-cyan-500/30">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-md bg-cyan-500/20 flex items-center justify-center">
                                        <Building2 className="w-6 h-6 text-cyan-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wider">🏦 Vendas PIX/Cartão (BANCO)</p>
                                        <p className="text-2xl font-bold text-cyan-500">{formatCurrency(totalBanco)}</p>
                                        <p className="text-xs text-gray-500">Depositado na conta bancária</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Manual Movements */}
                    <Card className="bg-[#121212] border-[#262626]">
                        <CardHeader>
                            <CardTitle className="text-white font-heading flex items-center gap-2">
                                <DollarSign className="w-5 h-5 text-rr-yellow-500" />
                                Movimentações Manuais
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex gap-3">
                                <Button
                                    className="bg-emerald-500 hover:bg-emerald-400 text-white"
                                    onClick={() => { setMovementType('reforco'); setMovementDialog(true); }}
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Reforço
                                </Button>
                                <Button
                                    variant="outline"
                                    className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                                    onClick={() => { setMovementType('sangria'); setMovementDialog(true); }}
                                >
                                    <Minus className="w-4 h-4 mr-2" />
                                    Retirada
                                </Button>
                                <Button
                                    variant="outline"
                                    className="border-orange-500/30 text-orange-500 hover:bg-orange-500/10"
                                    onClick={() => { setMovementType('despesa'); setMovementDialog(true); }}
                                >
                                    <Receipt className="w-4 h-4 mr-2" />
                                    Despesa
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Movements List */}
                    <Card className="bg-[#121212] border-[#262626]">
                        <CardHeader>
                            <CardTitle className="text-white font-heading flex items-center gap-2">
                                <History className="w-5 h-5 text-rr-yellow-500" />
                                Movimentações do Caixa
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ScrollArea className="h-[400px]">
                                <div className="space-y-2">
                                    {movements.map((movement) => {
                                        const config = MOVEMENT_TYPES[movement.movement_type] || MOVEMENT_TYPES.venda_dinheiro;
                                        const Icon = config.icon;
                                        return (
                                            <div 
                                                key={movement.id}
                                                className="flex items-center justify-between p-3 bg-[#0a0a0a] rounded-md border border-[#1a1a1a]"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-md flex items-center justify-center ${config.color.split(' ')[0]}`}>
                                                        <Icon className={`w-4 h-4 ${config.color.split(' ')[1]}`} />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <Badge className={config.color}>{config.label}</Badge>
                                                            <span className="text-xs text-gray-500">{formatTime(movement.created_at)}</span>
                                                        </div>
                                                        <p className="text-sm text-gray-400 mt-0.5">{movement.reason}</p>
                                                        {movement.observation && (
                                                            <p className="text-xs text-gray-500 mt-0.5">Obs: {movement.observation}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className={`font-bold ${movement.amount >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                                        {movement.amount >= 0 ? '+' : ''}{formatCurrency(movement.amount)}
                                                    </p>
                                                    <p className="text-xs text-gray-500">{movement.created_by_name}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {movements.length === 0 && (
                                        <div className="text-center py-12 text-gray-500">
                                            <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                            <p>Nenhuma movimentação registrada</p>
                                        </div>
                                    )}
                                </div>
                            </ScrollArea>
                        </CardContent>
                    </Card>
                </>
            )}

            {/* No Register Open Message */}
            {!currentRegister && (
                <Card className="bg-[#121212] border-[#262626]">
                    <CardContent className="py-12 text-center">
                        <Lock className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-white mb-2">Caixa Fechado</h3>
                        <p className="text-gray-500 mb-6">Abra o caixa para começar a registrar movimentações</p>
                        <Button
                            className="bg-rr-yellow-500 hover:bg-rr-yellow-400 text-black font-bold"
                            onClick={() => setOpenRegisterDialog(true)}
                        >
                            <Unlock className="w-4 h-4 mr-2" />
                            Abrir Caixa
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Open Register Dialog */}
            <Dialog open={openRegisterDialog} onOpenChange={setOpenRegisterDialog}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-heading flex items-center gap-2">
                            <Unlock className="w-5 h-5 text-rr-yellow-500" />
                            Abrir Caixa
                        </DialogTitle>
                        <DialogDescription className="text-gray-400">
                            Informe o valor inicial do fundo de troco
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <div className="space-y-2">
                            <Label className="text-gray-400">Valor Inicial (Fundo de Troco)</Label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={initialBalance}
                                    onChange={(e) => setInitialBalance(e.target.value)}
                                    className="pl-10 bg-[#121212] border-[#262626] text-white text-lg"
                                    placeholder="0,00"
                                    autoFocus
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpenRegisterDialog(false)} className="border-[#262626] text-gray-400">
                            Cancelar
                        </Button>
                        <Button onClick={handleOpenRegister} disabled={processing} className="bg-rr-yellow-500 hover:bg-rr-yellow-400 text-black font-bold">
                            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Abrir Caixa'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Close Register Dialog */}
            <Dialog open={closeRegisterDialog} onOpenChange={setCloseRegisterDialog}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-heading flex items-center gap-2">
                            <Lock className="w-5 h-5 text-red-500" />
                            Fechar Caixa
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        {/* Summary */}
                        <div className="p-4 bg-[#121212] rounded-md space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-400">Fundo Inicial</span>
                                <span className="text-white">{formatCurrency(currentRegister?.initial_balance)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Total Entradas</span>
                                <span className="text-emerald-500">+{formatCurrency(currentRegister?.total_entries)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Total Saídas</span>
                                <span className="text-red-500">-{formatCurrency(currentRegister?.total_exits)}</span>
                            </div>
                            <div className="flex justify-between pt-2 border-t border-[#262626]">
                                <span className="text-white font-bold">Saldo Esperado</span>
                                <span className="text-rr-yellow-500 font-bold">{formatCurrency(currentRegister?.expected_balance)}</span>
                            </div>
                        </div>

                        {/* Counted Balance Input */}
                        <div className="space-y-2">
                            <Label className="text-gray-400">Valor Contado no Caixa</Label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={countedBalance}
                                    onChange={(e) => setCountedBalance(e.target.value)}
                                    className="pl-10 bg-[#121212] border-[#262626] text-white text-lg"
                                    placeholder="0,00"
                                    autoFocus
                                />
                            </div>
                        </div>

                        {/* Difference Preview */}
                        {countedBalance && currentRegister && (
                            <div className={`p-4 rounded-md ${
                                parseFloat(countedBalance) === currentRegister.expected_balance 
                                    ? 'bg-emerald-500/10 border border-emerald-500/30' 
                                    : parseFloat(countedBalance) > currentRegister.expected_balance
                                        ? 'bg-blue-500/10 border border-blue-500/30'
                                        : 'bg-red-500/10 border border-red-500/30'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-400">Diferença</span>
                                    <span className={`font-bold text-lg ${
                                        parseFloat(countedBalance) === currentRegister.expected_balance 
                                            ? 'text-emerald-500' 
                                            : parseFloat(countedBalance) > currentRegister.expected_balance
                                                ? 'text-blue-500'
                                                : 'text-red-500'
                                    }`}>
                                        {parseFloat(countedBalance) >= currentRegister.expected_balance ? '+' : ''}
                                        {formatCurrency(parseFloat(countedBalance) - currentRegister.expected_balance)}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    {parseFloat(countedBalance) === currentRegister.expected_balance 
                                        ? '✅ Caixa batendo!' 
                                        : parseFloat(countedBalance) > currentRegister.expected_balance
                                            ? 'Sobra no caixa'
                                            : '⚠️ Falta no caixa'}
                                </p>
                                {/* Alert for atendente with divergence */}
                                {isAtendente() && parseFloat(countedBalance) !== currentRegister.expected_balance && (
                                    <div className="mt-3 p-3 bg-red-500/20 rounded-md border border-red-500/30">
                                        <div className="flex items-center gap-2 text-red-500">
                                            <AlertTriangle className="w-4 h-4" />
                                            <span className="text-sm font-bold">Bloqueado para Atendente</span>
                                        </div>
                                        <p className="text-xs text-red-400 mt-1">
                                            Caixa com divergência só pode ser fechado por um administrador.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCloseRegisterDialog(false)} className="border-[#262626] text-gray-400">
                            Cancelar
                        </Button>
                        <Button 
                            onClick={handleCloseRegister} 
                            disabled={processing || (isAtendente() && countedBalance && parseFloat(countedBalance) !== currentRegister?.expected_balance)} 
                            className="bg-red-500 hover:bg-red-400 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar Fechamento'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Movement Dialog */}
            <Dialog open={movementDialog} onOpenChange={setMovementDialog}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-heading flex items-center gap-2">
                            {movementType === 'reforco' && <Plus className="w-5 h-5 text-emerald-500" />}
                            {movementType === 'sangria' && <Minus className="w-5 h-5 text-amber-500" />}
                            {movementType === 'despesa' && <Receipt className="w-5 h-5 text-orange-500" />}
                            {movementType === 'reforco' ? 'Reforço de Caixa' : movementType === 'sangria' ? 'Retirada de Caixa' : 'Despesa Rápida'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <div className="space-y-2">
                            <Label className="text-gray-400">Valor *</Label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={movementAmount}
                                    onChange={(e) => setMovementAmount(e.target.value)}
                                    className="pl-10 bg-[#121212] border-[#262626] text-white"
                                    placeholder="0,00"
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-gray-400">Motivo *</Label>
                            <Input
                                value={movementReason}
                                onChange={(e) => setMovementReason(e.target.value)}
                                className="bg-[#121212] border-[#262626] text-white"
                                placeholder={movementType === 'reforco' ? 'Ex: Troco do banco' : movementType === 'sangria' ? 'Ex: Retirada para depósito' : 'Ex: Compra de material'}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-gray-400">Observação (opcional)</Label>
                            <Input
                                value={movementObservation}
                                onChange={(e) => setMovementObservation(e.target.value)}
                                className="bg-[#121212] border-[#262626] text-white"
                                placeholder="Observação adicional..."
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setMovementDialog(false); resetMovementForm(); }} className="border-[#262626] text-gray-400">
                            Cancelar
                        </Button>
                        <Button 
                            onClick={handleCreateMovement} 
                            disabled={processing}
                            className={
                                movementType === 'reforco' 
                                    ? 'bg-emerald-500 hover:bg-emerald-400 text-white' 
                                    : movementType === 'sangria'
                                        ? 'bg-amber-500 hover:bg-amber-400 text-black'
                                        : 'bg-orange-500 hover:bg-orange-400 text-white'
                            }
                        >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* History Dialog */}
            <Dialog open={historyDialog} onOpenChange={setHistoryDialog}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-heading flex items-center gap-2">
                            <History className="w-5 h-5 text-rr-yellow-500" />
                            Histórico de Caixas
                        </DialogTitle>
                    </DialogHeader>
                    <ScrollArea className="h-[500px] py-4">
                        <div className="space-y-3">
                            {history.map((register) => (
                                <div 
                                    key={register.id}
                                    className="p-4 bg-[#121212] rounded-md border border-[#1a1a1a] hover:border-rr-yellow-500/30 cursor-pointer transition-colors"
                                    onClick={() => openRegisterDetails(register)}
                                >
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <Badge className={register.status === 'aberto' ? 'bg-emerald-500/20 text-emerald-500' : 'bg-gray-500/20 text-gray-400'}>
                                                    {register.status === 'aberto' ? 'ABERTO' : 'FECHADO'}
                                                </Badge>
                                                <span className="text-sm text-gray-500">
                                                    {formatDateTime(register.opened_at)}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-400 mt-1">
                                                Por: {register.opened_by_name}
                                                {register.closed_by_name && ` → ${register.closed_by_name}`}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-rr-yellow-500 font-bold">{formatCurrency(register.expected_balance)}</p>
                                            {register.difference !== null && (
                                                <p className={`text-sm ${register.difference === 0 ? 'text-emerald-500' : register.difference > 0 ? 'text-blue-500' : 'text-red-500'}`}>
                                                    Diferença: {register.difference >= 0 ? '+' : ''}{formatCurrency(register.difference)}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {history.length === 0 && (
                                <div className="text-center py-12 text-gray-500">
                                    <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>Nenhum histórico disponível</p>
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>

            {/* Details Dialog */}
            <Dialog open={detailsDialog} onOpenChange={setDetailsDialog}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-heading flex items-center gap-2">
                            <Receipt className="w-5 h-5 text-rr-yellow-500" />
                            Detalhes do Caixa
                        </DialogTitle>
                    </DialogHeader>
                    {selectedRegister && (
                        <ScrollArea className="h-[500px] py-4">
                            <div className="space-y-4">
                                {/* Summary */}
                                <div className="p-4 bg-[#121212] rounded-md space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Status</span>
                                        <Badge className={selectedRegister.cash_register?.status === 'aberto' ? 'bg-emerald-500/20 text-emerald-500' : 'bg-gray-500/20 text-gray-400'}>
                                            {selectedRegister.cash_register?.status?.toUpperCase()}
                                        </Badge>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Fundo Inicial</span>
                                        <span className="text-white">{formatCurrency(selectedRegister.cash_register?.initial_balance)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Total Entradas</span>
                                        <span className="text-emerald-500">+{formatCurrency(selectedRegister.cash_register?.total_entries)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Total Saídas</span>
                                        <span className="text-red-500">-{formatCurrency(selectedRegister.cash_register?.total_exits)}</span>
                                    </div>
                                    <div className="flex justify-between pt-2 border-t border-[#262626]">
                                        <span className="text-white font-bold">Saldo Esperado</span>
                                        <span className="text-rr-yellow-500 font-bold">{formatCurrency(selectedRegister.cash_register?.expected_balance)}</span>
                                    </div>
                                    {selectedRegister.cash_register?.counted_balance !== null && (
                                        <>
                                            <div className="flex justify-between">
                                                <span className="text-gray-400">Valor Contado</span>
                                                <span className="text-white">{formatCurrency(selectedRegister.cash_register?.counted_balance)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-400">Diferença</span>
                                                <span className={selectedRegister.cash_register?.difference === 0 ? 'text-emerald-500' : selectedRegister.cash_register?.difference > 0 ? 'text-blue-500' : 'text-red-500'}>
                                                    {selectedRegister.cash_register?.difference >= 0 ? '+' : ''}{formatCurrency(selectedRegister.cash_register?.difference)}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Movements by Type */}
                                {selectedRegister.by_type && Object.keys(selectedRegister.by_type).length > 0 && (
                                    <div className="p-4 bg-[#121212] rounded-md">
                                        <h4 className="text-sm font-bold text-gray-400 mb-3">Resumo por Tipo</h4>
                                        <div className="space-y-2">
                                            {Object.entries(selectedRegister.by_type).map(([type, data]) => {
                                                const config = MOVEMENT_TYPES[type] || { label: type, color: 'text-gray-400' };
                                                return (
                                                    <div key={type} className="flex justify-between items-center">
                                                        <span className={`text-sm ${config.color.split(' ')[1]}`}>{config.label}</span>
                                                        <span className="text-white">{data.count}x - {formatCurrency(data.total)}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Movement List */}
                                {selectedRegister.movements && selectedRegister.movements.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-bold text-gray-400 mb-3">Todas Movimentações</h4>
                                        <div className="space-y-2">
                                            {selectedRegister.movements.map((movement) => {
                                                const config = MOVEMENT_TYPES[movement.movement_type] || MOVEMENT_TYPES.venda_dinheiro;
                                                return (
                                                    <div key={movement.id} className="flex justify-between items-center p-2 bg-[#121212] rounded">
                                                        <div>
                                                            <Badge className={config.color}>{config.label}</Badge>
                                                            <span className="text-xs text-gray-500 ml-2">{formatTime(movement.created_at)}</span>
                                                        </div>
                                                        <span className={movement.amount >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                                                            {movement.amount >= 0 ? '+' : ''}{formatCurrency(movement.amount)}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </ScrollArea>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CashRegisterPage;
